<template>
  <dialog-form
    :show-dialog="isShowDialog"
    @cancel="hideDialog"
    @submit="submit"
  >
    <template #title>
      Additional Information
    </template>

    <fieldset>
      <div class="row mobile-mb-0">
        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isVisuallyImpaired"
            v-model="additionalInfo.isVisuallyImpaired"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Visually Impaired
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isCane"
            v-model="additionalInfo.isCane"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Uses a Cane / Leg braces
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isElbowAssist"
            v-model="additionalInfo.isElbowAssist"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            <el-tooltip
              class="item"
              effect="dark"
              placement="top"
              popper-class="custom-tooltip note-info"
            >
              <template #content>
                Check this box to send a message to each assigned driver requesting assistance in and out of the vehicle.
                A $5 fee per ride applies, delivered to the driver as a tip.
              </template>
              <span> Needs Elbow Assist <i class="fa fa-info-circle absolute-top-right" /> </span>
            </el-tooltip>
          </el-checkbox>
        </div>
        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isElbowAssist"
            v-model="additionalInfo.isTraveling"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Is Traveling
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isWalker"
            v-model="additionalInfo.isWalker"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Uses a Walker / Crutches
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isWheelchair"
            v-model="additionalInfo.isWheelchair"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Manual Wheelchair
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isPowerWheelchairScooter"
            v-model="additionalInfo.isPowerWheelchairScooter"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Power Wheelchair / Scooter
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isHardOfHearing"
            v-model="additionalInfo.isHardOfHearing"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Hard of Hearing
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isServiceDog"
            v-model="additionalInfo.isServiceDog"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Service Dog<span v-show="additionalInfo.isServiceDog">&nbsp;&nbsp;🐶</span>
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isOxygenTank"
            v-model="additionalInfo.isOxygenTank"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Portable Oxygen Tank / Respirator
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isPoorMemory"
            v-model="additionalInfo.isPoorMemory"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Poor Memory
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isNoBigCars"
            v-model="additionalInfo.isNoBigCars"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Cannot get into big cars
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isNoSmallCars"
            v-model="additionalInfo.isNoSmallCars"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Cannot get into small cars
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isVerballyImpaired"
            v-model="additionalInfo.isVerballyImpaired"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Verbally Impaired
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isSlowWalker"
            v-model="additionalInfo.isSlowWalker"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Slow Walker
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isAda"
            v-model="additionalInfo.isAda"
            :disabled="additionalInfo.noAdditionalInfo"
          >
            Has ADA Eligible Disability
          </el-checkbox>
        </div>

        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="noAdditionalInfo"
            v-model="additionalInfo.noAdditionalInfo"
          >
            None
          </el-checkbox>
        </div>
      </div>

      <!-- Validation message -->
      <div
        v-if="!hasSelection"
        class="validation-error"
      >
        <i class="fa fa-warning" /> Please select at least one option or choose "None"
      </div>
    </fieldset>
  </dialog-form>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useSwalDialog } from '@/use/swalDialog';
import { CallerAdditionalInfo } from '@/models/caller';

const props = defineProps({
  isShowDialog: {
    type: Boolean,
    default: false,
  },
  customerInfo: {
    type: Object,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { showConfirmPopupDialog } = useSwalDialog();

const baseAdditionalInfo: CallerAdditionalInfo = {
  isVisuallyImpaired: false,
  isCane: false,
  isElbowAssist: false,
  isTraveling: false,
  isWalker: false,
  isWheelchair: false,
  isPowerWheelchairScooter: false,
  isHardOfHearing: false,
  isServiceDog: false,
  isOxygenTank: false,
  isPoorMemory: false,
  isNoBigCars: false,
  isNoSmallCars: false,
  isVerballyImpaired: false,
  isSlowWalker: false,
  isAda: false,
  isSpeech: false,
  noAdditionalInfo: false,
};

const additionalInfo = ref({ ...baseAdditionalInfo });
watch(() => props.customerInfo, () => {
  for (const k of Object.keys(baseAdditionalInfo)) {
    additionalInfo.value[k as keyof CallerAdditionalInfo] = Boolean(props.customerInfo?.[k])
  }
}, { immediate: true });

// Check if at least one option is selected
const hasSelection = computed(() => {
  return Object.keys(additionalInfo.value).some((key) => additionalInfo.value[key as keyof CallerAdditionalInfo]);
});

const submit = () => {
  if (!hasSelection.value) {
    return;
  }

  emit('submit', additionalInfo.value);
};

const hideDialog = () => {
  emit('cancel');
};

const showSpeechRecognitionPopup = async (value: boolean) => {
  if (!value) {
    additionalInfo.value.isSpeech = false;
    return;
  }

  const isConfirmed = await showConfirmPopupDialog({
    text: 'Because you selected "visually impaired", would you like to activate voice commands for when you call in? Voice command allows you to press or say commands when you call GoGo.  For example “press or say 1 to get picked up from your home.”',
    title: 'Confirmed with the customer',
  });

  additionalInfo.value.isSpeech = !!isConfirmed;
};

const disableAllOptionsExceptNoneOption = async (value: boolean) => {
  if (!value) {
    return;
  }

  // Disable all other options and set them to false
  Object.keys(additionalInfo.value).forEach((key) => {
    if (key !== 'noAdditionalInfo') {
      additionalInfo.value[key as keyof CallerAdditionalInfo] = false;
    }
  });
};

watch(() => additionalInfo.value.isVisuallyImpaired, showSpeechRecognitionPopup);

watch(() => additionalInfo.value.noAdditionalInfo, disableAllOptionsExceptNoneOption);
</script>
